import axios from 'axios';
import * as Sentry from '@sentry/react';

const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
        'Content-Type': 'application/json'
    },
    timeout: 15000,
});

// Interceptor para adicionar o token JWT aos cabeçalhos
api.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// Helpers
const wait = (ms: number) => new Promise(res => setTimeout(res, ms));
const reportAxiosError = (error: any) => {
    const cfg = error?.config || {};
    const dataPreview = typeof cfg.data === 'string' && cfg.data.length > 300 ? cfg.data.slice(0, 300) + '…' : cfg.data;
    Sentry.captureException(error, (scope) => {
        scope.setLevel('error');
        scope.setTag('api.client', 'api');
        scope.setExtras({
            url: cfg.baseURL ? `${cfg.baseURL}${cfg.url || ''}` : cfg.url,
            method: cfg.method,
            timeout: cfg.timeout,
            data: dataPreview,
            isOnline: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        });
        return scope;
    });
};

// Interceptor para tratamento de erros
api.interceptors.response.use(
    response => response,
    async (error) => {
        if (error.response) {
            // Se o erro for 401 (não autorizado), limpe o localStorage e redirecione para login
            if (error.response.status === 401) {
                // Caso especial: o usuário foi removido da empresa que tinha selecionada
                const isSessionRevoked = error.response.data?.code === 'SESSION_COMPANY_REVOKED';
                localStorage.clear();
                window.location.href = isSessionRevoked
                    ? '/signin?reason=session-expired'
                    : '/signin';
            }
            console.error('Erro na resposta:', error.response.data);
        } else if (error.request) {
            console.error('Sem resposta do servidor');
            reportAxiosError(error);
        } else {
            console.error('Erro na configuração da requisição:', error.message);
        }
        // Retry seguro para GET em erro de rede/timeout
        const config = error.config || {};
        const isGet = (config.method || 'get').toLowerCase() === 'get';
        const isNetwork = error.code === 'ERR_NETWORK' || error.message === 'Network Error';
        const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
        config.__retryCount = config.__retryCount || 0;

        if ((isNetwork || isTimeout) && isGet && config.__retryCount < 1) {
            config.__retryCount += 1;
            await wait(500);
            try {
                return await api.request(config);
            } catch (retryErr) {
                reportAxiosError(retryErr);
                return Promise.reject(retryErr);
            }
        }

        return Promise.reject(error);
    }
);

export default api;